<template>
  <Modal title="" :max-width="720" :header-border="false" body-overflow="auto" body-max-height="600px" :body-padding="false" @close="onClose">
    <template #header>
      <div class="cd__title-wrap">
        <span class="cd-source">
          <span class="cd-source__mark" :style="sourceStyle">
            <component :is="sourceIconComponent" />
          </span>
          {{ sourceLabel }}
        </span>
        <div class="cd__name">{{ titleDisplay }}</div>
        <div class="cd__sub">
          <template v-for="(seg, i) in subSegments" :key="i">
            <span v-if="i > 0" class="cd__dot" />
            <span>{{ seg }}</span>
          </template>
        </div>
      </div>
    </template>

    <!-- Gmail email signals → email layout -->
    <template v-if="isGmailEmail">
      <div class="cd-email-body">
        <div class="cd-addr">
          <div class="cd-addr__row">
            <span class="cd-addr__k">From</span>
            <span class="cd-addr__v">{{ gmailFrom }}</span>
          </div>
          <div class="cd-addr__row">
            <span class="cd-addr__k">To</span>
            <span class="cd-addr__v">{{ gmailTo }}</span>
          </div>
          <div v-if="gmailCc" class="cd-addr__row">
            <span class="cd-addr__k">Cc</span>
            <span class="cd-addr__v">{{ gmailCc }}</span>
          </div>
        </div>
        <div class="cd-prose">{{ gmailBody }}</div>
      </div>
    </template>

    <!-- Job change signals  -->
    <template v-if="isJobChange">
      <div class="cd-signal-body">
        <div class="cd-notes">
          <div class="cd-note">
            <span class="cd-note__k">Job change</span>
            <span class="cd-note__v">{{ signalDisplayName }}</span>
          </div>
          <div class="cd-note">
            <span class="cd-note__k">{{ contactName }}</span>
            <span class="cd-note__v">{{ contactTitle }}</span>
          </div>
          <div v-if="previousContact?.id" class="cd-note">
            <span class="cd-note__k" v-if="isChampion">Previously a Champion</span>
            <span class="cd-note__k" v-else>Previously</span>
            <span class="cd-note__v">{{ previousContact.display?.email || previousContact?.email || ''}}</span>
            <span class="cd-note__v">{{ previousContact.display?.title || ''}}</span>
          </div>
        </div>
      </div>
    </template>

    <!-- Generic signal → structured layout -->
    <template v-else>
      <div class="cd-signal-body">
        <div class="cd-notes">
          <div class="cd-note">
            <span class="cd-note__k">Signal</span>
            <span class="cd-note__v">{{ signalDisplayName }}</span>
          </div>
          <div v-if="sourceName" class="cd-note">
            <span class="cd-note__k">Source</span>
            <span class="cd-note__v">{{ sourceName }}</span>
          </div>
          <div v-if="occurredAt" class="cd-note">
            <span class="cd-note__k">Occurred</span>
            <span class="cd-note__v">{{ occurredAt }}</span>
          </div>
        </div>

        <div v-if="prettyDescription" class="cd-signal-desc">
          <div class="cd-note__k" style="margin-bottom: 8px;">Description</div>
          <pre class="cd-signal-json">{{ prettyDescription }}</pre>
        </div>

        <!-- Trigger runs -->
        <div v-if="triggerRuns.length > 0" class="cd-signal-section">
          <div class="cd-note__k" style="margin-bottom: 8px;">Triggers fired</div>
          <div class="cd-trigger-list">
            <div v-for="r in triggerRuns" :key="r.run_id" class="cd-trigger-row" @click="openRun(r.run_id)">
              <div class="cd-trigger-top">
                <span class="cd-trigger-name">{{ r.trigger_name || 'Trigger' }}</span>
                <span v-if="r.agent_name" class="cd-trigger-arrow">&rarr;</span>
                <span v-if="r.agent_name" class="cd-trigger-agent">{{ r.agent_name }}</span>
              </div>
              <div v-if="r.created_at" class="cd-trigger-time">{{ formatTs(r.created_at) }}</div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <button class="cd-btn cd-btn--ghost" @click="onClose">Close</button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, h, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import Modal from '../../next/components/Modal.vue'
import { apiFetch } from '../../services/api'
import { useContextDrawer } from '../useContextDrawer'

const { contextGeneration } = useContextDrawer()

const props = defineProps<{
  signal: Record<string, any>
  onClose: () => void
}>()

const router = useRouter()

// ── Signal type detection ──────────────────────────────────────

const signalName = computed(() => String(props.signal?.name || '').trim())

const isGmailEmail = computed(() =>
  signalName.value === 'gmail.inbound_email' || signalName.value === 'gmail.outbound_email'
)

const isOutbound = computed(() => {
  if (isGmailEmail.value) {
    return signalName.value.includes('outbound')
  }
  const d = props.signal?.description
  return d?.direction === 'out'
})

const isJobChange = computed(() => 
  signalName.value.startsWith('jobchange.')
)

const isChampion = computed(() =>
  props.signal?.source_type === 'sfdc_champions_search' ||
    props.signal?.source_type === 'hubspot_champions_search'
)

const previousContact = ref<any>({})

async function loadPreviousContact() {
  previousContact.value = {}
  const id = props.signal?.description?.old_contact_id
  const aid = props.signal?.description?.old_account_id
  if (!id || !aid) return
  try {
    previousContact.value = await apiFetch(`/accounts/${aid}/contacts/${id}`)
  } catch {
  }
}

watch(() => props.signal?.description?.old_contact_id, (id) => {
  if (id) void loadPreviousContact()
}, { immediate: true })

// ── Source badge ────────────────────────────────────────────────

const sourceLabel = computed(() => {
  if (isGmailEmail.value) return isOutbound.value ? 'Email sent' : 'Email received'
  const src = String(props.signal?.source_label || props.signal?.source_type || '').trim().toLowerCase()
  if (src.includes('salesforce')) return 'Salesforce'
  if (src.includes('hubspot')) return 'HubSpot'
  if (src.includes('warmly')) return 'Warmly'
  if (src.includes('gmail') || src.includes('google')) return 'Gmail'
  return 'Signal'
})

const sourceStyle = computed(() => {
  // Email reads as email, not as the vendor that synced it
  if (isGmailEmail.value) {
    return { background: isOutbound.value ? '#94a3b8' : '#3b82f6', color: '#fff' }
  }
  const label = sourceLabel.value.toLowerCase()
  if (label === 'gmail') return { background: '#ea4335', color: '#fff' }
  if (label === 'salesforce') return { background: '#0d9dda', color: '#fff' }
  if (label === 'hubspot') return { background: '#ff7a59', color: '#fff' }
  if (label === 'warmly') return { background: '#6366f1', color: '#fff' }
  return { background: '#64748b', color: '#fff' }
})

// Simple generic icon
const genericIcon = () => h('svg', { viewBox: '0 0 16 16', fill: 'currentColor' }, [
  h('path', { d: 'M8 2a6 6 0 110 12A6 6 0 018 2zm0 1.5a4.5 4.5 0 100 9 4.5 4.5 0 000-9zM8 5a1 1 0 110 2 1 1 0 010-2zm0 3.5c.41 0 .75.34.75.75v2a.75.75 0 01-1.5 0v-2c0-.41.34-.75.75-.75z' }),
])
const gmailIcon = () => h('svg', { viewBox: '0 0 16 16', fill: 'currentColor' }, [
  h('path', { d: 'M2 4.5A1.5 1.5 0 013.5 3h9A1.5 1.5 0 0114 4.5v7a1.5 1.5 0 01-1.5 1.5h-9A1.5 1.5 0 012 11.5v-7zm1.2.3v6.7h9.6V4.8L8 8.5 3.2 4.8zm.8-.3L8 7.5l4-3H4z' }),
])

const sourceIconComponent = computed(() => isGmailEmail.value ? gmailIcon : genericIcon)

// ── Title ──────────────────────────────────────────────────────

const desc = computed(() => {
  const d = props.signal?.description
  return d && typeof d === 'object' ? d : {}
})

const titleDisplay = computed(() => {
  if (isGmailEmail.value) {
    return String(desc.value.subject || 'Email').trim() || 'Email'
  }
  if (isJobChange.value) {
    if (props.signal?.name === 'jobchange.arrival'){
      if (isChampion.value) {
        return "Champion arrived at their new Account"
      }
      return "Contact arrived at their new Account"
    }
    if (props.signal?.name === 'jobchange.departure'){
      if (isChampion.value) {
        return "Champion left the Account"
      }
      return "Contact left the Account"
    }
  }
  return String(props.signal?.display_name || props.signal?.name || 'Signal').trim()
})

const signalDisplayName = computed(() => {
  const raw = signalName.value
  // Turn "salesforce.campaign_member_status_change" → "Campaign Member Status Change"
  return raw.replace(/^[a-z]+\./, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
})

// ── Sub-segments ───────────────────────────────────────────────

const subSegments = computed(() => {
  const parts: string[] = []
  if (isGmailEmail.value) {
    parts.push(isOutbound.value ? 'Sent' : 'Received')
  }
  const raw = props.signal?.occurred_at
  if (raw) {
    try {
      const dt = new Date(raw)
      parts.push(dt.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }))
      if (!isJobChange.value) {
        parts.push(dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }))
      }
    } catch { /* skip */ }
  }
  if (isJobChange.value) {
    if (isChampion.value) {
      const rn = reportName.value
      if (rn) {
          parts.push(`Identified as a Champion by the '${rn}' report`)
      } else {
          parts.push("Identified as a Champion by querying your Salesforce")
      }
    }
  }
  return parts
})

// ── Gmail email fields ─────────────────────────────────────────

const gmailFrom = computed(() => String(desc.value.from_header || desc.value.from_email || '').trim() || '—')
const gmailTo = computed(() => String(desc.value.to || '').trim() || '—')
const gmailCc = computed(() => String(desc.value.cc || '').trim())
const gmailBody = computed(() => String(desc.value.body || '').trim())

// ── Job change fields ─────────────────────────────────────────

const contactName = computed(() => String(desc.value.name || '').trim())
const contactTitle = computed(() => String(desc.value.title || '').trim())
const reportName = computed(() => String(desc.value.report_name || '').trim())

// ── Generic signal fields ──────────────────────────────────────

const sourceName = computed(() => String(props.signal?.source_label || props.signal?.source_type || '').trim())
const occurredAt = computed(() => formatTs(props.signal?.occurred_at))

const prettyDescription = computed(() => {
  const d = props.signal?.description
  try {
    return JSON.stringify(d ?? {}, null, 2)
  } catch {
    return String(d || '')
  }
})

function formatTs(ts: any): string {
  if (!ts) return ''
  try { return new Date(ts).toLocaleString() } catch { return String(ts) }
}

// ── Trigger runs ───────────────────────────────────────────────

const triggerRuns = ref<any[]>([])

async function loadTriggerRuns(signalId: string) {
  triggerRuns.value = []
  if (!signalId || Number(props.signal?.triggered_count || 0) <= 0) return
  const gen = contextGeneration.value
  try {
    const resp = await apiFetch(`/signals/${signalId}/trigger_runs`)
    if (gen !== contextGeneration.value) return
    triggerRuns.value = Array.isArray(resp?.runs) ? resp.runs : []
  } catch {
    if (gen !== contextGeneration.value) return
    triggerRuns.value = []
  }
}

function openRun(runId: string) {
  const id = String(runId || '').trim()
  if (!id) return
  router.push(`/agent-runs/${encodeURIComponent(id)}`)
}

watch(() => props.signal?.id, (id) => {
  if (id) void loadTriggerRuns(String(id))
}, { immediate: true })
</script>

<style scoped>
.cd-loading {
  padding: var(--space-5);
  color: var(--color-text-tertiary);
  font-size: var(--fs-sm);
}

/* Email layout (Gmail signals) */
.cd-email-body {
  display: flex;
  flex-direction: column;
}

.cd-addr {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--color-border-subtle);
}

.cd-addr__row {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 10px;
  align-items: baseline;
}

.cd-addr__k {
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--color-text-tertiary);
}

.cd-addr__v {
  font-size: var(--fs-sm);
  color: var(--color-text-primary);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  word-break: break-word;
}

.cd-prose {
  padding: var(--space-5);
  font-size: var(--fs-sm);
  line-height: var(--lh-loose);
  color: var(--color-text-primary);
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 80px;
}

/* Generic signal layout */
.cd-signal-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-5);
}

.cd-signal-desc {
  display: flex;
  flex-direction: column;
}

.cd-signal-json {
  margin: 0;
  padding: var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  background: var(--color-bg-subtle);
  max-height: 300px;
  overflow: auto;
  font-size: var(--fs-xs);
  line-height: var(--lh-normal);
  color: var(--color-text-primary);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.cd-trigger-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cd-trigger-row {
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  background: var(--color-bg-surface);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cd-trigger-row:hover {
  background: var(--color-bg-subtle);
}

.cd-trigger-top {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.cd-trigger-name {
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-accent);
}

.cd-trigger-arrow {
  color: var(--color-text-tertiary);
}

.cd-trigger-agent {
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--color-text-primary);
}

.cd-trigger-time {
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
}
</style>
